import isEqual from 'lodash/isEqual'

import { getCpUpdates } from '../api/case'
import { fetchClient, fetchProtectedClientFields, getClientByVersion } from '../api/client'
import { fetchMortgageByVersion, fetchMortgages } from '../api/mortgage'
import { fetchPropertyByVersion, getProperty } from '../api/property'
import request from '../requesters/default'
import { formatClient } from '../utils/schemaMapping/client'

// computeCpUpdates: Extracted from Case resolver to keep resolver concise.
export default async function computeCpUpdates(caseId: string) {
  const updates = await getCpUpdates(caseId)

  if (!updates || !updates.cp_changes) return updates

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  const entityIdKeys = ['id', 'address_id', 'property_id', 'mortgage_id', 'client_id', 'internalHash', 'internal_hash']

  // Normalizes mixed dot/bracket notation into consistent dot paths (e.g. foo[0] -> foo.0)
  const normalizeFieldPath = (field: string) => (field || '').replace(/\[(\d+)\]/g, '.$1')

  // Attempts to resolve a path on an entity while returning whether the field actually exists
  const resolveFieldWithPresence = (entity: any, fieldPath: string) => {
    if (!entity || !fieldPath) return { found: false, value: undefined }

    const tryResolve = (root: any, field: string) => {
      const path = normalizeFieldPath(field)
      const tokens = path.split('.').filter(Boolean)
      let current: any = root

      for (const token of tokens) {
        if (UUID_REGEX.test(token)) {
          if (Array.isArray(current)) {
            const found = current.find((el) => el && entityIdKeys.some((k) => (el as any)[k] === token))
            if (!found) return { found: false, value: undefined }
            current = found
            continue
          }
          if (current && typeof current === 'object') {
            const found = Object.values(current).find(
              (val) => val && typeof val === 'object' && entityIdKeys.some((k) => (val as any)[k] === token),
            )
            if (!found) return { found: false, value: undefined }
            current = found
            continue
          }
          return { found: false, value: undefined }
        }

        if (current && typeof current === 'object' && token in current) {
          current = current[token]
          continue
        }

        if (Array.isArray(current) && !isNaN(Number(token))) {
          current = current[Number(token)]
          continue
        }

        return { found: false, value: undefined }
      }

      return { found: true, value: current }
    }

    const rootVal = tryResolve(entity, fieldPath)
    if (rootVal.found) return rootVal
    if (entity && typeof entity === 'object' && entity.details) {
      const detailsVal = tryResolve(entity.details, fieldPath)
      if (detailsVal.found) return detailsVal
      const prefixedVal = tryResolve(entity, `details.${fieldPath}`)
      if (prefixedVal.found) return prefixedVal
    }

    return { found: false, value: undefined }
  }

  // Memoize versioned snapshots and "current" entities to avoid duplicate network calls
  const versionCache = new Map<string, any>()
  const currentCache = new Map<string, any>()
  const keyForVersion = (entityType: string, id: string, v: number, show?: string[] | undefined) =>
    `${entityType}:${id}:v${v}:show:${(show || []).join(',')}`
  const keyForCurrent = (entityType: string, id: string, show?: string[] | undefined) =>
    `${entityType}:${id}:current:${(show || []).join(',')}`

  // Fetches a specific entity snapshot by version, respecting protected-field visibility where needed
  const fetchVersionEntity = async (
    entityType: string,
    id: string,
    version?: number,
    show_protected?: string[] | undefined,
    force?: boolean,
  ) => {
    if (!version) return null
    const key = keyForVersion(entityType, id, version, show_protected)

    if (!force && versionCache.has(key)) {
      return await versionCache.get(key)
    }

    const p = (async () => {
      try {
        if (entityType === 'clients') {
          if (show_protected && show_protected.length) {
            const qp = show_protected.map((f) => `show_protected=.${f}`).join('&')
            const url = qp ? `/client/${id}/version/${version}?${qp}` : `/client/${id}/version/${version}`
            const response = await request.get(url)
            if (!response.data) return null
            return response.data.client ? formatClient(response.data.client) : null
          }

          return await getClientByVersion(id, version)
        }
        if (entityType === 'mortgages') {
          return await fetchMortgageByVersion({ mortgage_id: id, version })
        }
        if (entityType === 'properties') {
          return await fetchPropertyByVersion({ property_id: id, version })
        }
      } catch (e) {
        console.error('Error fetching versioned entity:', e)
        return null
      }
      return null
    })()

    versionCache.set(key, p)

    const res = await p
    versionCache.set(key, res)
    return res
  }

  // Fetches the latest version of an entity for comparison with historical snapshots
  const fetchCurrentEntity = async (entityType: string, id: string, show_protected?: string[] | undefined) => {
    const key = keyForCurrent(entityType, id, show_protected)

    if (currentCache.has(key)) {
      return await currentCache.get(key)
    }

    const p = (async () => {
      try {
        if (entityType === 'clients') {
          const res =
            show_protected && show_protected.length
              ? await fetchProtectedClientFields(id, show_protected)
              : await fetchClient({ client_ids: [id], client_details: true })
          return res
        }

        if (entityType === 'mortgages') {
          const arr = await fetchMortgages({ mortgage_ids: [id] })
          const res = (arr && arr[0]) || null
          currentCache.set(key, res)
          return res
        }

        if (entityType === 'properties') {
          const obj = await getProperty({ property_ids: [id] })
          return (obj && obj.properties && obj.properties[0]) || null
        }
      } catch (e) {
        console.error('Error fetching current entity:', e)
        return null
      }
      return null
    })()

    currentCache.set(key, p)
    const res = await p
    currentCache.set(key, res)
    return res
  }

  // Fields that should be treated as scalars even if they resemble nested objects elsewhere
  const scalarFieldNames = new Set<string>(['date_of_birth'])
  const isScalarValue = (v: any) => v === null || typeof v !== 'object' || v instanceof Date

  // Attempts to coerce numeric-like strings into numbers to reduce noise in diffs
  const normalizeNumberLike = (v: any) => {
    if (v == null) return v
    if (typeof v === 'string') {
      const s = v.trim().replace(/,/g, '')
      if (/^-?\d+(?:\.\d+)?$/.test(s)) return Number(s)
      return s
    }
    if (typeof v === 'number') return v
    return v
  }

  // Recursively normalizes values so structural comparisons are tolerant of numeric formatting differences
  const deepNormalize = (v: any): any => {
    if (v == null) return v
    if (typeof v === 'string' || typeof v === 'number') return normalizeNumberLike(v)
    if (Array.isArray(v)) return v.map((el) => deepNormalize(el))
    if (typeof v === 'object') {
      const out: any = {}
      for (const [k, val] of Object.entries(v)) {
        out[k] = deepNormalize(val)
      }
      return out
    }
    return v
  }

  // Produces a structured diff between two values, preserving detail about arrays/objects
  const computeDifferences = (previous: any, current: any, path?: string): any => {
    if (previous == null && current == null) return null

    if (isScalarValue(previous) && isScalarValue(current)) {
      // If the runtime types differ (e.g. string vs number) consider this
      // a real change — we want to preserve type changes like
      // "400.55" -> 400.55 as differences. Only if types match do we
      // attempt tolerant numeric-like normalization for equality.
      if (typeof previous !== typeof current) {
        return { kind: 'scalar', field: path ?? undefined, from: previous ?? null, to: current ?? null }
      }
      const a = normalizeNumberLike(previous)
      const b = normalizeNumberLike(current)
      if (isEqual(a, b)) return null
    }

    if (isEqual(previous, current)) return null

    if (
      isScalarValue(previous) ||
      isScalarValue(current) ||
      Array.isArray(previous) !== Array.isArray(current) ||
      (previous && current && previous.constructor !== current.constructor)
    ) {
      return { kind: 'scalar', field: path ?? undefined, from: previous ?? null, to: current ?? null }
    }

    if (Array.isArray(previous) || Array.isArray(current)) {
      const prev = Array.isArray(previous) ? previous : []
      const curr = Array.isArray(current) ? current : []

      const hasIdentifiableObjects = (arr: any[]) =>
        arr.some(
          (el) =>
            el &&
            typeof el === 'object' &&
            entityIdKeys.some((k) => {
              const val = (el as any)[k]
              return val !== undefined && val !== null
            }),
        )

      if (hasIdentifiableObjects(prev) || hasIdentifiableObjects(curr)) {
        const prevMap = new Map<string | number, any>()
        const currMap = new Map<string | number, any>()

        const keyFor = (el: any, idx: number) => {
          if (!el || typeof el !== 'object') return idx
          for (const k of entityIdKeys) {
            const val = (el as any)[k]
            if (val !== undefined && val !== null) return String(val)
          }
          return idx
        }

        prev.forEach((el, i) => prevMap.set(keyFor(el, i), el))
        curr.forEach((el, i) => currMap.set(keyFor(el, i), el))

        const allKeys = Array.from(new Set([...prevMap.keys(), ...currMap.keys()]))
        const changes: any[] = []
        for (const k of allKeys) {
          const prevElement = prevMap.get(k)
          const currElement = currMap.get(k)
          const elementField = path ? `${path}.${k}` : String(k)
          const nested = computeDifferences(prevElement, currElement, elementField)
          if (nested !== null) {
            const changeType = prevElement === undefined ? 'added' : currElement === undefined ? 'removed' : 'changed'
            changes.push({ id: k, type: changeType, field: elementField, change: nested })
          }
        }

        return changes.length ? { kind: 'array', field: path ?? undefined, changes } : null
      }

      const max = Math.max(prev.length, curr.length)
      const changes: any[] = []
      for (let i = 0; i < max; i++) {
        const prevElement = i < prev.length ? prev[i] : undefined
        const currElement = i < curr.length ? curr[i] : undefined
        const elementField = path ? `${path}[${i}]` : `[${i}]`
        const nested = computeDifferences(prevElement, currElement, elementField)
        if (nested !== null) {
          changes.push({ index: i, field: elementField, change: nested })
        }
      }
      return changes.length ? { kind: 'array', field: path ?? undefined, changes } : null
    }

    const prevKeys = previous ? Object.keys(previous) : []
    const currKeys = current ? Object.keys(current) : []
    const all = Array.from(new Set([...prevKeys, ...currKeys]))
    const changes: any[] = []
    for (const k of all) {
      const prevValue = previous ? previous[k] : undefined
      const currValue = current ? current[k] : undefined

      if (scalarFieldNames.has(k)) {
        if (!isEqual(prevValue, currValue)) {
          const fieldPath = path ? `${path}.${k}` : k
          changes.push({
            key: k,
            change: { kind: 'scalar', field: fieldPath, from: prevValue ?? null, to: currValue ?? null },
          })
        }
        continue
      }

      const nested = computeDifferences(prevValue, currValue, path ? `${path}.${k}` : k)
      if (nested !== null) {
        changes.push({ key: k, change: nested })
      }
    }
    return changes.length ? { kind: 'object', field: path ?? undefined, changes } : null
  }

  // Expose test helpers to allow running isolated diffs in a debug script.
  // Not used by production code; safe to keep for local debugging.
  ;(computeDifferences as any).__debug = {
    computeDifferences,
    deepNormalize,
    normalizeNumberLike,
  }

  /* resolvePathValues removed — logic consolidated into computeChangeForEntry */

  // Strict path resolver that does a direct traversal of the provided entity
  // without falling back to `details`. Use this when we have both versioned
  // snapshots to guarantee we compare the exact values from each version.
  // Resolves a path without `details` fallbacks to ensure we compare exact snapshot payloads
  const getValueDirect = (entity: any, fieldPath: string) => {
    if (!entity || !fieldPath) return { found: false, value: undefined }
    const path = normalizeFieldPath(fieldPath)
    const tokens = path.split('.').filter(Boolean)
    let current: any = entity

    for (const token of tokens) {
      if (UUID_REGEX.test(token)) {
        if (Array.isArray(current)) {
          const found = current.find((el) => el && entityIdKeys.some((k) => (el as any)[k] === token))
          if (!found) return { found: false, value: undefined }
          current = found
          continue
        }
        return { found: false, value: undefined }
      }

      if (current && typeof current === 'object' && token in current) {
        current = current[token]
        continue
      }

      if (Array.isArray(current) && !isNaN(Number(token))) {
        current = current[Number(token)]
        continue
      }

      return { found: false, value: undefined }
    }

    return { found: true, value: current }
  }

  // Search for the first occurrence of a simple key within a nested entity
  // structure. This is a best-effort fallback used when a field like
  // `monthly_payment` may have moved under `calculated_values` between
  // versions. It intentionally returns the first match found and should
  // only be used as a fallback when strict resolution fails.
  const searchForKeyInEntity = (entity: any, key: string): { found: boolean; value: any; path?: string } => {
    if (entity == null || typeof key !== 'string' || key === '') return { found: false, value: undefined }

    const seen = new Set<any>()
    const stack: Array<{ node: any; path: string }> = [{ node: entity, path: '' }]

    while (stack.length) {
      const { node, path } = stack.pop() as any
      if (node == null) continue
      if (seen.has(node)) continue
      seen.add(node)

      if (typeof node === 'object') {
        if (Object.prototype.hasOwnProperty.call(node, key)) {
          return { found: true, value: (node as any)[key], path: path ? `${path}.${key}` : key }
        }
        if (Array.isArray(node)) {
          for (let i = 0; i < node.length; i++) {
            stack.push({ node: node[i], path: `${path}[${i}]` })
          }
        } else {
          for (const k of Object.keys(node)) {
            stack.push({ node: (node as any)[k], path: path ? `${path}.${k}` : k })
          }
        }
      }
    }

    return { found: false, value: undefined }
  }

  // Harmonizes previous/current candidate values after resolution so downstream diffing
  // logic can reason about presence flags in a single place
  const finalizeValues = (
    fromEntity: any,
    toEntity: any,
    currentEntity: any,
    entityId: string,
    toVersion: number | undefined,
    resolved: any,
    showProtected?: string[],
  ) => {
    let { previousPresent, toPresent, latestPresent, previousValue, toValue, latestValue } = resolved

    const simplePathTokens = normalizeFieldPath(resolved && resolved.pathTokens ? resolved.pathTokens.join('.') : '')
      .split('.')
      .filter(Boolean)
    const isSimpleTopLevel = !resolved.containsUuid && simplePathTokens.length === 1
    if (isSimpleTopLevel) {
      const key = simplePathTokens[0]
      if (
        fromEntity &&
        !Object.prototype.hasOwnProperty.call(fromEntity, key) &&
        !(fromEntity && fromEntity.details && Object.prototype.hasOwnProperty.call(fromEntity.details, key))
      ) {
        previousPresent = false
        previousValue = undefined
      }
      if (
        toEntity &&
        !Object.prototype.hasOwnProperty.call(toEntity, key) &&
        !(toEntity && toEntity.details && Object.prototype.hasOwnProperty.call(toEntity.details, key))
      ) {
        toPresent = false
        toValue = undefined
      }
      if (
        currentEntity &&
        !Object.prototype.hasOwnProperty.call(currentEntity, key) &&
        !(currentEntity && currentEntity.details && Object.prototype.hasOwnProperty.call(currentEntity.details, key))
      ) {
        latestPresent = false
        latestValue = undefined
      }
    }

    const getEntityId = (obj: any) => {
      if (!obj || typeof obj !== 'object') return undefined
      for (const k of entityIdKeys) {
        const v = (obj as any)[k]
        if (v !== undefined && v !== null) return String(v)
      }
      return undefined
    }

    try {
      const prevRootId = getEntityId(fromEntity) || (entityId ? String(entityId) : undefined)
      const toRootId = getEntityId(toEntity)
      const latestRootId = getEntityId(currentEntity)
      if (prevRootId) {
        if (toVersion !== undefined && toRootId && toRootId !== prevRootId) {
          toPresent = false
          toValue = undefined
        }
        if (toVersion === undefined && latestRootId && latestRootId !== prevRootId) {
          latestPresent = false
          latestValue = undefined
        }
      }
    } catch (e) {
      console.error('Error comparing entity ids for change detection:', e)
    }

    const previous = previousPresent ? previousValue : null
    let currentPresent = false
    let currentValue: any = null
    if (toPresent) {
      currentPresent = true
      currentValue = toValue
    } else if (latestPresent) {
      currentPresent = true
      currentValue = latestValue
    } else {
      currentPresent = false
      currentValue = null
    }

    if (
      toVersion === undefined &&
      showProtected &&
      showProtected.length &&
      latestPresent &&
      latestValue !== undefined
    ) {
      currentPresent = true
      currentValue = latestValue
    }

    return { previous, currentPresent, currentValue, previousPresent, toPresent, latestPresent }
  }

  // Builds a single change object for a CP update entry, handling snapshot fetching,
  // field resolution, normalization, and final diff construction
  const computeChangeForEntry = async (entityType: string, entry: any) => {
    const {
      entity_id: entityId,
      editor_id: editorId,
      editor_type: editorType,
      field,
      version_from: versionFrom,
      version_to: versionTo,
    } = entry || {}
    const fromVersion = versionFrom ? Number(versionFrom) : undefined
    const toVersion = versionTo ? Number(versionTo) : undefined

    const protectedFieldCandidates = ['date_of_birth', 'previous_names', 'accounts']
    let showProtected: string[] | undefined = undefined
    if (entityType === 'clients' && field) {
      const tokens = normalizeFieldPath(field).split('.').filter(Boolean)
      const candidateMatchesTokens = (candidate: string, tokensArr: string[]) => {
        const parts = candidate.split('.')
        let lastIndex = -1
        for (const part of parts) {
          const nextIndex = tokensArr.indexOf(part, lastIndex + 1)
          if (nextIndex === -1) return false
          lastIndex = nextIndex
        }
        return true
      }

      const found = protectedFieldCandidates.filter((p) => candidateMatchesTokens(p, tokens))
      if (found.length) {
        showProtected = Array.from(new Set(found.map((f) => f.split('.')[0])))
      }
    }

    const [fromEntity, toEntity] = await Promise.all([
      fetchVersionEntity(entityType, entityId, fromVersion, showProtected),
      fetchVersionEntity(entityType, entityId, toVersion, showProtected),
    ])

    const currentEntity = await fetchCurrentEntity(entityType, entityId, showProtected)

    // Note: detailed snapshot body logging was removed to avoid leaking
    // potentially sensitive response payloads into logs. Keep only
    // lightweight presence checks elsewhere when needed.

    const path = field || ''
    const pathTokens = normalizeFieldPath(path).split('.').filter(Boolean)
    const containsUuid = pathTokens.some((t) => UUID_REGEX.test(t))

    let previousValue: any = undefined
    let toValue: any = undefined
    let latestValue: any = undefined
    let previousPresent = false
    let toPresent = false
    let latestPresent = false

    if (containsUuid) {
      const uuidIndex = pathTokens.findIndex((t) => UUID_REGEX.test(t))
      const arrayPrefix = pathTokens.slice(0, uuidIndex).join('.')
      const tail = pathTokens.slice(uuidIndex + 1).join('.')

      const findElement = (entity: any, token: string) => {
        if (!entity) return { found: false, value: undefined }
        const arrRes = resolveFieldWithPresence(entity, arrayPrefix)
        const arrDetailsRes =
          entity && entity.details
            ? resolveFieldWithPresence(entity.details, arrayPrefix)
            : { found: false, value: undefined }
        const arr = (arrRes.found ? arrRes.value : undefined) || (arrDetailsRes.found ? arrDetailsRes.value : undefined)
        if (!Array.isArray(arr)) return { found: false, value: undefined }
        const found = arr.find((el: any) => el && entityIdKeys.some((k) => (el as any)[k] === token))
        if (!found) return { found: false, value: undefined }
        if (!tail) return { found: true, value: found }
        return resolveFieldWithPresence(found, tail)
      }

      const uuidToken = pathTokens[uuidIndex]

      const prevRes = fromEntity ? findElement(fromEntity, uuidToken) : { found: false, value: undefined }
      const toRes = toEntity ? findElement(toEntity, uuidToken) : { found: false, value: undefined }
      const latestRes = currentEntity ? findElement(currentEntity, uuidToken) : { found: false, value: undefined }

      previousPresent = prevRes.found
      toPresent = toRes.found
      latestPresent = latestRes.found

      previousValue = prevRes.value
      toValue = toRes.value
      latestValue = latestRes.value
    } else {
      const prevRes = fromEntity
        ? resolveFieldWithPresence(fromEntity, field || '')
        : { found: false, value: undefined }
      const toRes = toEntity ? resolveFieldWithPresence(toEntity, field || '') : { found: false, value: undefined }
      const latestRes = currentEntity
        ? resolveFieldWithPresence(currentEntity, field || '')
        : { found: false, value: undefined }

      previousPresent = prevRes.found
      toPresent = toRes.found
      latestPresent = latestRes.found

      previousValue = prevRes.value
      toValue = toRes.value
      latestValue = latestRes.value
    }

    // If we have both versioned snapshots available, prefer strict direct
    // traversal from those snapshots to avoid `details` fallbacks that can
    // hide moved fields (e.g. top-level `monthly_payment` vs nested
    // `calculated_values.monthly_payments`). Only override if direct
    // resolution yields a value.
    if (fromVersion !== undefined && toVersion !== undefined) {
      try {
        const dPrev = getValueDirect(fromEntity, field || '')
        if (dPrev.found) {
          previousPresent = true
          previousValue = dPrev.value
        }
        const dTo = getValueDirect(toEntity, field || '')
        if (dTo.found) {
          toPresent = true
          toValue = dTo.value
        }
        const dLatest = getValueDirect(currentEntity, field || '')
        if (dLatest.found) {
          latestPresent = true
          latestValue = dLatest.value
        }
      } catch (e) {
        console.error(e)
        // ignore strict resolution failures
      }
    }

    const simplePathTokens = normalizeFieldPath(field || '')
      .split('.')
      .filter(Boolean)
    const isSimpleTopLevel = !containsUuid && simplePathTokens.length === 1
    if (isSimpleTopLevel) {
      const key = simplePathTokens[0]
      if (
        fromEntity &&
        !Object.prototype.hasOwnProperty.call(fromEntity, key) &&
        !(fromEntity && fromEntity.details && Object.prototype.hasOwnProperty.call(fromEntity.details, key))
      ) {
        previousPresent = false
        previousValue = undefined
      }

      if (
        toEntity &&
        !Object.prototype.hasOwnProperty.call(toEntity, key) &&
        !(toEntity && toEntity.details && Object.prototype.hasOwnProperty.call(toEntity.details, key))
      ) {
        toPresent = false
        toValue = undefined
      }

      if (
        currentEntity &&
        !Object.prototype.hasOwnProperty.call(currentEntity, key) &&
        !(currentEntity && currentEntity.details && Object.prototype.hasOwnProperty.call(currentEntity.details, key))
      ) {
        latestPresent = false
        latestValue = undefined
      }
    }

    // Fallback: if simple top-level key wasn't found in the expected
    // locations, attempt to search the entity for the key anywhere. This
    // helps when fields move under `calculated_values.*` between versions.
    if (isSimpleTopLevel) {
      const key = simplePathTokens[0]
      if (!previousPresent && fromEntity) {
        const s = searchForKeyInEntity(fromEntity, key)
        if (s.found) {
          previousPresent = true
          previousValue = s.value
        }
      }
      if (!toPresent && toEntity) {
        const s = searchForKeyInEntity(toEntity, key)
        if (s.found) {
          toPresent = true
          toValue = s.value
        }
      }
      if (!latestPresent && currentEntity) {
        const s = searchForKeyInEntity(currentEntity, key)
        if (s.found) {
          latestPresent = true
          latestValue = s.value
        }
      }
    }

    const getEntityId = (obj: any) => {
      if (!obj || typeof obj !== 'object') return undefined
      for (const k of entityIdKeys) {
        const v = (obj as any)[k]
        if (v !== undefined && v !== null) return String(v)
      }
      return undefined
    }

    try {
      const prevRootId = getEntityId(fromEntity) || (entityId ? String(entityId) : undefined)
      const toRootId = getEntityId(toEntity)
      const latestRootId = getEntityId(currentEntity)
      if (prevRootId) {
        // If the newer snapshot refers to a different root entity, clear
        // the newer-side value and mark it as not-present so we compare the
        // same logical entity across versions.
        if (toVersion !== undefined && toRootId && toRootId !== prevRootId) {
          toValue = undefined
          toPresent = false
        }
        if (toVersion === undefined && latestRootId && latestRootId !== prevRootId) {
          latestValue = undefined
          latestPresent = false
        }
      }
    } catch (e) {
      console.error('Error comparing entity ids for change detection:', e)
    }

    // Finalize canonical previous/current candidates using the centralized
    // helper. This avoids duplication and ensures consistent rules are
    // applied across code paths.
    const {
      previous: previousCandidate,
      currentPresent,
      currentValue: currentCandidate,
      previousPresent: previousPresentFinal,
    } = finalizeValues(
      fromEntity,
      toEntity,
      currentEntity,
      entityId,
      toVersion,
      {
        previousPresent,
        toPresent,
        latestPresent,
        previousValue,
        toValue,
        latestValue,
        containsUuid,
        pathTokens,
      },
      showProtected,
    )

    // Removed detailed candidate logging to keep logs compact and avoid
    // exposing full snapshot payloads. Presence flags are still used
    // internally for change detection.

    // Compare both sides (previousCandidate and currentCandidate) to build a
    // structural `differences` object. We keep both original versions intact
    // through this process.
    let differences = computeDifferences(previousCandidate, currentCandidate, field || undefined)

    if (differences == null) {
      try {
        const prevNorm = deepNormalize(previousCandidate)
        const currNorm = deepNormalize(currentCandidate)
        if (!isEqual(prevNorm, currNorm)) {
          const structural = computeDifferences(prevNorm, currNorm, field || undefined)
          differences = structural || {
            kind: 'scalar',
            field: field || undefined,
            from: previousCandidate ?? null,
            to: currentCandidate ?? null,
          }
        }
      } catch (e) {
        console.error('Error in fallback normalisation diff:', e)
      }
    }

    // Determine change type. Prioritise created/deleted (presence flags),
    // then if both sides exist use `differences` to decide 'changed'. If
    // no differences, mark 'unchanged'. This prevents normalization-only
    // equality checks from hiding structural differences that we computed
    // earlier.
    let change_type: 'deleted' | 'created' | 'changed' | 'unchanged'
    if (previousPresentFinal && !currentPresent) change_type = 'deleted'
    else if (!previousPresentFinal && currentPresent) change_type = 'created'
    else if (differences != null) change_type = 'changed'
    else change_type = 'unchanged'

    // Build result: include differences and always attach `previous` and
    // `current` payloads so callers (and downstream logic) have both
    // versions available for inspection — important for mortgages.
    const result: any = {
      entity_type: entityType,
      entity_id: entityId,
      editor_id: editorId,
      editor_type: editorType,
      field,
      version_from: versionFrom,
      version_to: versionTo,
      // differences may be null when nothing changed; keep the key
      // present and explicitly set to null in that case so callers
      // always have a predictable shape.
      differences: differences ?? null,
      // Always include previous and current (may be `null` when not present)
      previous: previousCandidate ?? null,
      current: currentCandidate ?? null,
      change_type,
    }

    if ((result as any).__diagnostic_version_mismatch) {
      result.change_type = 'changed'
      result.__diagnostic_version_mismatch = true
    }

    return result
  }

  // Process each supported entity type and attach enriched change data to the response
  const entities = ['clients', 'mortgages', 'properties']
  const out: any = { cp_changes: {} }

  for (const entity of entities) {
    const list = updates.cp_changes[entity]
    if (!list || !list.length) {
      out.cp_changes[entity] = []
      continue
    }

    out.cp_changes[entity] = await Promise.all(list.map((entry: any) => computeChangeForEntry(entity, entry)))
  }

  out.cp_changes.documents = updates.cp_changes.documents || []

  return out
}
